<script setup lang="ts">
import { computed, ref } from "vue";
import { UiText, UiIcon, UiButton, UiInfoBox } from "@shopexperts/shared";
import type { AdminExpertOrganization } from "@shopexperts/legacy-api";
import { useAdminOrganizationWallet, useAdminOrganizationWalletLevel } from "@/app/composables/wallet";
import { formatCurrency } from "@/v2Projects/utils/display";
import { formatBps } from "@/components/expert/wallet/walletTransactionDisplay";
import WalletSummaryLevelCard from "@/components/expert/wallet/WalletSummaryLevelCard.vue";
import ReassignExpertLevelDialog from "./ReassignExpertLevelDialog.vue";

const props = defineProps<{
  expertId: number;
  organization?: AdminExpertOrganization | null;
  organizationId: number | null | undefined;
  expertName?: string | null;
}>();

const emit = defineEmits<{
  (e: "create-transaction"): void;
}>();

const organizationId = computed(() => props.organizationId);
const hasOrg = computed(() => typeof organizationId.value === "number" && organizationId.value > 0);
const organizationLabel = computed(() => {
  if (props.organization?.name) return props.organization.name;
  return hasOrg.value ? `Organization #${organizationId.value}` : null;
});

const {
  availableBalanceCents,
  holdBalanceCents,
  lifetimeGrossCents,
  lifetimeNetCents,
  currency,
  isLoading: isWalletLoading,
  error: walletError,
} = useAdminOrganizationWallet({ organizationId });

const {
  currentLevel,
  nextLevel,
  hasNextLevel,
  lifetimeGrossCents: levelLifetimeGrossCents,
  remainingGrossCents: levelRemainingGrossCents,
  currency: levelCurrency,
  isLoading: isLevelLoading,
  error: levelError,
} = useAdminOrganizationWalletLevel({
  organizationId,
  enabled: hasOrg,
});

const reassignOpen = ref(false);

const hasWallet = computed(() => hasOrg.value && !walletError.value);

const availableLabel = computed(() =>
  hasWallet.value ? formatCurrency(availableBalanceCents.value, currency.value) : "—",
);
const heldLabel = computed(() =>
  hasWallet.value ? formatCurrency(holdBalanceCents.value, currency.value) : "—",
);
const grossLabel = computed(() =>
  hasWallet.value ? formatCurrency(lifetimeGrossCents.value, currency.value) : "—",
);
const netLabel = computed(() =>
  hasWallet.value ? formatCurrency(lifetimeNetCents.value, currency.value) : "—",
);

const secondaryStats = computed(() => [
  {
    key: "held",
    label: "On hold",
    value: heldLabel.value,
    tooltip: "Funds reserved on the wallet and not yet available for payout.",
  },
  {
    key: "gross",
    label: "Lifetime gross",
    value: grossLabel.value,
    tooltip: "Lifetime gross sales — this drives the expert's auto-enrolled level.",
  },
  {
    key: "net",
    label: "Lifetime net",
    value: netLabel.value,
    tooltip: "Lifetime earnings after platform commission and processing fees.",
  },
]);

const currentTitle = computed(() => currentLevel.value?.public_title ?? "No level yet");

const commissionLabel = computed(() => {
  const bps = currentLevel.value?.commission_rate_bps;
  if (bps == null) return null;
  return formatBps(bps);
});

const targetGrossCents = computed(() => {
  if (!hasNextLevel.value || levelRemainingGrossCents.value === null) {
    return levelLifetimeGrossCents.value;
  }

  return levelLifetimeGrossCents.value + Math.max(0, levelRemainingGrossCents.value);
});

const percent = computed(() => {
  if (!hasNextLevel.value) return 100;
  if (targetGrossCents.value <= 0) return 0;
  return Math.min(100, Math.max(0, (levelLifetimeGrossCents.value / targetGrossCents.value) * 100));
});

const percentLabel = computed(() => `${Math.round(percent.value)}%`);

const remainingToNextLevelLabel = computed(() => {
  if (!hasNextLevel.value || levelRemainingGrossCents.value === null || !nextLevel.value) return null;
  const remaining = formatCurrency(Math.max(0, levelRemainingGrossCents.value), levelCurrency.value ?? currency.value);
  return `${remaining} more in gross sales to reach ${nextLevel.value.public_title}`;
});

const progressCaption = computed(() => {
  if (!hasNextLevel.value) return "At the highest level (by gross-sales threshold).";
  const progressCurrency = levelCurrency.value ?? currency.value;
  const earned = formatCurrency(levelLifetimeGrossCents.value, progressCurrency);
  const target = formatCurrency(targetGrossCents.value, progressCurrency);
  return `${earned} of ${target} in gross sales`;
});

const levelTooltip = computed(() =>
  currentLevel.value?.public_description ??
  "Loaded from the expert organization's wallet level endpoint. Use Change level to override manually.",
);
</script>

<template>
  <section class="font-archivo flex flex-col gap-3">
    <div class="flex items-center justify-between gap-3">
      <div class="flex flex-col gap-1">
        <div class="flex items-center gap-2">
          <UiIcon name="wallet" class="h-4 w-4 shrink-0 text-se-gray-500" />
          <UiText
            as="h2"
            variant="override"
            text="Wallet & level"
            class="text-[11px] font-semibold uppercase tracking-[0.14em] text-se-gray-500"
          />
        </div>
        <UiText
          v-if="organizationLabel"
          as="p"
          variant="p-small"
          class="text-se-gray-600"
          :text="organizationLabel"
        />
      </div>
      <div v-if="hasOrg" class="flex items-center gap-2">
        <UiButton
          variant="outline"
          size="sm"
          label="New transaction"
          icon="plus-square"
          @click="emit('create-transaction')"
        />
        <UiButton
          variant="primary"
          size="sm"
          label="Change level"
          icon="sparkle"
          @click="reassignOpen = true"
        />
      </div>
    </div>

    <!-- No organization linked -->
    <UiInfoBox v-if="!hasOrg" variant="warning" title="No wallet available">
      <UiText as="span" variant="p-small">
        <span>
          This expert has no linked organization, so wallet balances, transactions, and level
          progress can't be loaded.
        </span>
      </UiText>
    </UiInfoBox>

    <UiInfoBox v-else-if="walletError" variant="error" title="Couldn't load wallet">
      <UiText as="span" variant="p-small">
        <span>The wallet failed to load. Refresh the page to try again.</span>
      </UiText>
    </UiInfoBox>

    <WalletSummaryLevelCard
      v-else
      :available-label="availableLabel"
      balance-caption="Available for payout"
      :secondary-stats="secondaryStats"
      :is-wallet-loading="isWalletLoading"
      :current-level="currentLevel"
      :next-level="nextLevel"
      :current-title="currentTitle"
      :commission-label="commissionLabel"
      :is-level-loading="isLevelLoading"
      :has-level-error="Boolean(levelError)"
      :level-tooltip="levelTooltip"
      :percent="percent"
      :percent-label="percentLabel"
      :progress-caption="progressCaption"
      :remaining-to-next-level-label="remainingToNextLevelLabel"
    />

    <ReassignExpertLevelDialog
      v-model="reassignOpen"
      :expert-id="expertId"
      :organization-id="organizationId"
      :expert-name="expertName"
      :current-level-id="currentLevel?.id ?? null"
    />
  </section>
</template>
